/**
 * Environment Variables Wrapper
 * Provides access to environment variables in both web and Capacitor builds
 * Falls back to import.meta.env for development, uses generated config for production
 */

// Try to import generated config (created during build)
let generatedConfig: Record<string, string> = {};
let generatedEnv: Record<string, string> = {};

try {
  // In production builds, use the generated config
  const configModule = require('./env.config.json');
  if (configModule?.env) {
    generatedConfig = configModule.env;
    generatedEnv = configModule.env;
  }
} catch (error) {
  // Config file doesn't exist yet, will use import.meta.env
  console.debug('[EnvConfig] Generated config not found, using import.meta.env');
}

// Function to get environment variable with fallback
export function getEnvVar(key: string, defaultValue?: string): string {
  // 1. Try generated config (from build-time env vars)
  if (generatedEnv[key]) {
    return generatedEnv[key];
  }
  
  // 2. Try window.__ENV_CONFIG__ (injected at runtime if available)
  if (typeof window !== 'undefined' && (window as any).__ENV_CONFIG__?.[key]) {
    return (window as any).__ENV_CONFIG__[key];
  }
  
  // 3. Try import.meta.env (Vite's way)
  const viteValue = (import.meta as any).env?.[key];
  if (viteValue) {
    return viteValue;
  }
  
  // 4. Fallback to default
  return defaultValue || '';
}

// Export individual environment variables for convenience
export const ENV = {
  SUPABASE_URL: getEnvVar('VITE_SUPABASE_URL', 'https://afvnwwsqnkbxradvcyln.supabase.co'),
  SUPABASE_ANON_KEY: getEnvVar('VITE_SUPABASE_ANON_KEY', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFmdm53d3NxbmtieHJhZHZjeWxuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAzNzMzNjIsImV4cCI6MjA2NTk0OTM2Mn0.JwpS8eL3Jkt9pKpxghx8l2hZjZOz6rKeRn9EXHRwQ3A'),
  OPENROUTER_API_KEY: getEnvVar('VITE_OPENROUTER_API_KEY'),
  OPENROUTER_DEFAULT_MODEL: getEnvVar('VITE_OPENROUTER_DEFAULT_MODEL', 'meta-llama/llama-4-maverick:free'),
  OPENROUTER_BACKUP_MODEL: getEnvVar('VITE_OPENROUTER_BACKUP_MODEL', 'google/gemma-3-27b-it:free'),
  OPENROUTER_BACKUP2_MODEL: getEnvVar('VITE_OPENROUTER_BACKUP2_MODEL', 'tngtech/deepseek-r1t2-chimera:free'),
  OPENROUTER_BACKUP3_MODEL: getEnvVar('VITE_OPENROUTER_BACKUP3_MODEL', 'deepseek/deepseek-chat-v3-0324:free'),
  OPENROUTER_BACKUP4_MODEL: getEnvVar('VITE_OPENROUTER_BACKUP4_MODEL', 'deepseek/deepseek-r1-0528:free'),
  OPENROUTER_DEV_KEY: getEnvVar('VITE_OPENROUTER_DEV_KEY'),
  TOGETHER_AI_KEY: getEnvVar('VITE_TOGETHER_AI_KEY'),
  POLLINATION_KEY: getEnvVar('VITE_POLLINATION_KEY'),
  AUTONOMOUS_BACKEND_URL: getEnvVar('VITE_AUTONOMOUS_BACKEND_URL', 'https://autonomous-ai-production.up.railway.app'),
  RAILWAY_API_URL: getEnvVar('VITE_RAILWAY_API_URL', 'https://autonomous-ai-production.up.railway.app'),
  ENVIRONMENT: getEnvVar('VITE_ENVIRONMENT', 'production'),
  ENABLE_DEBUG_LOGS: getEnvVar('VITE_ENABLE_DEBUG_LOGS', 'false'),
};

// Debug function to log environment status
export function debugEnvStatus() {
  console.log('[EnvConfig] Environment Variable Status:');
  console.log('  Generated Config:', Object.keys(generatedEnv).length > 0 ? '✅ Loaded' : '❌ Not found');
  console.log('  VITE_SUPABASE_URL:', ENV.SUPABASE_URL ? '✅ Set' : '❌ Missing');
  console.log('  VITE_OPENROUTER_API_KEY:', ENV.OPENROUTER_API_KEY ? '✅ Set' : '❌ Missing');
  console.log('  VITE_TOGETHER_AI_KEY:', ENV.TOGETHER_AI_KEY ? '✅ Set' : '❌ Missing');
}

// Make available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).__ENV_DEBUG__ = debugEnvStatus;
  (window as any).__ENV_CONFIG__ = ENV;
}

export default ENV;

